import { useEffect, useRef, useState } from 'react';
import { api } from './api';

/**
 * When Callie records a change (an availability week, trip answers, an
 * applied proposal), the page behind her panel must not go stale — a
 * physician who then taps Submit on an old board would undo what she just
 * saved. She announces the change here; pages that show server data listen
 * and reload.
 */
const EVENT = 'callcovered:changed';

/**
 * The same announcement, carried to the platform's OTHER TABS (D93).
 *
 * A `window` event never leaves the tab that fired it, and a coordinator keeps two open — the
 * grid in one, the home page in the other. Without this, editing in one tab leaves the other
 * showing yesterday's answer until it is reloaded by hand, which is exactly the stale screen an
 * edit is supposed to prevent.
 *
 * Not supported everywhere and blocked in some privacy modes, so it is entirely optional: when
 * the channel cannot be made, announcements still work within the tab that fired them.
 */
const channel: BroadcastChannel | null = (() => {
  try {
    return typeof BroadcastChannel === 'function' ? new BroadcastChannel(EVENT) : null;
  } catch {
    return null;
  }
})();

if (channel) {
  // Arriving from another tab: raise it locally so every listener here is the same one line of
  // code. `fromAnotherTab` stops it being sent straight back out and round for ever.
  channel.onmessage = (e: MessageEvent) => {
    const detail = (e.data ?? { tools: [] }) as { tools: string[] };
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { ...detail, fromAnotherTab: true } }));
  };
}

export function announceChange(detail: { tools: string[] }) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail }));
  try {
    channel?.postMessage(detail);
  } catch {
    // A closed or blocked channel must never break the edit that just succeeded.
  }
}

/** Call `reload` again whenever a change is announced. */
export function useReloadOnChange(reload: () => void) {
  const latest = useRef(reload);
  latest.current = reload;
  useEffect(() => {
    const handler = () => latest.current();
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);
}

/** A counter that steps on every announced change — add it to an effect's deps to re-run the load. */
export function useChangeTick(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const handler = () => setTick((n) => n + 1);
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);
  return tick;
}

/**
 * Somebody else's change, on somebody else's machine (D94).
 *
 * Everything above carries a change within one browser. This is the other half: the server is
 * asked for a fingerprint of the caller's schedule, and when it differs from the last answer the
 * same announcement is raised locally — so every screen already listening reloads, with nothing
 * new to wire up.
 *
 * Twenty seconds, and only while the tab is actually being looked at. A hidden tab asks nothing
 * and catches up the moment it is shown again, so a coordinator with six tabs open overnight
 * costs nothing. Deliberately polling rather than a push: see the service's own note.
 */
const POLL_MS = 20_000;

export function useServerChanges(enabled: boolean) {
  const seen = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const check = async () => {
      if (!live) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try {
        const { version } = await api.changeVersion();
        // Claim the new version BEFORE deciding whether to announce it. Two checks can be in
        // flight at once — React runs effects twice in development, and a tab shown again asks
        // immediately while the interval's own request is still out. Reading first and writing
        // afterwards let both see the old value and both announce, which reloaded every screen
        // twice for one edit. Writing first means the second one finds its own answer already
        // recorded and says nothing.
        const previous = seen.current;
        seen.current = version;
        if (!live) return;
        // The first answer only establishes where we are; it is not news.
        if (previous !== null && previous !== version) {
          window.dispatchEvent(new CustomEvent(EVENT, { detail: { tools: ['elsewhere'], fromAnotherPerson: true } }));
        }
      } catch {
        // Signed out, offline, or the server is rolling: stay quiet and ask again next time. A
        // failure here must never surface as an error on a page the person is reading.
      }
    };

    const tick = () => {
      void check().finally(() => {
        if (live) timer = setTimeout(tick, POLL_MS);
      });
    };
    // Ask as soon as the tab is shown again, rather than waiting out the rest of the interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    tick();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
}
